import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { AnimatePresence } from "framer-motion";
import { FileWarning } from "lucide-react";
import { Observer, observer } from "mobx-react-lite";
import type { ReactNode } from "react";

import AnimatedElement from "./AnimatedElement";
import GeminiPromptWarning from "./GeminiPromptWarning";
import useMessageDragAndDrop from "./hooks/useMessageDragAndDrop";
import useMessageScrolling from "./hooks/useMessageScrolling";
import Message, { AddNewMessageBlock } from "./Message";
import { IMessageStore } from "./Message/message-store";
import MessagesFooter from "./MessagesFooter";
import MessagesHeader from "./MessagesHeader";
import { PlaceholderContainer } from "./PlaceholderContainer";
import SnippetDialog from "./SnippetDialog";
import { withSnippetDialog } from "./SnippetDialog/snippet-store";

import { usePlayground } from "@/components/Playground/playground-store";
import {
  TOUR_LOCK_SCROLL_PROPS,
  TOUR_TARGET_IDS,
  type TourTargetId,
} from "@/components/Playground/tour/constants";
import TourTargetContainer from "@/components/Playground/tour/TourTargetContainer";
import { ROLES } from "@/types";

// Only the first two tour steps target messages; later steps target sidebar UI.
const tourIdForMessageIndex = (index: number): TourTargetId | null => {
  const ids: readonly TourTargetId[] = [
    TOUR_TARGET_IDS.SYSTEM_MESSAGE,
    TOUR_TARGET_IDS.USER_MESSAGE,
  ];
  return ids[index] ?? null;
};

interface MessagesProps {
  messages: IMessageStore[];
  onAddMessage?: () => void;
  hideHeader?: boolean;
  title?: string;
  editorActions?: ReactNode;
  footerLeftContent?: ReactNode;
}

/**
 * Slim, sticky error hint that stays visible at the top of the messages
 * scroll area. Designed to be unobtrusive — just enough to catch attention
 * without overwhelming the workspace.
 */
const ErrorSummaryBanner = observer(() => {
  const playground = usePlayground();
  const { hasErrors, errorCount } = playground.parsingErrorSummary;

  if (!hasErrors) {
    return null;
  }

  return (
    <div className="sticky top-0 z-30 mb-[2px] flex items-center gap-2 border-b border-red-100 bg-red-50/80 px-3 py-1.5 backdrop-blur-sm">
      <FileWarning className="h-3.5 w-3.5 flex-shrink-0 text-red-500" strokeWidth={2} />
      <span className="text-[11px] text-red-700/90">
        {errorCount === 1
          ? "1 template error — see highlighted message below"
          : `${errorCount} template errors — see highlighted messages below`}
      </span>
    </div>
  );
});

const MessagesList = observer(() => {
  const playground = usePlayground();
  const { messagesEndRef } = useMessageScrolling();
  const {
    dragDisabled,
    draggingId,
    handleDragStart,
    handleDragEnd,
    handleDragUpdate,
    droppableRef,
  } = useMessageDragAndDrop();

  const shouldShowPlaceholder = playground.isChat && !playground.readonly;

  if (!playground.isChat) {
    return (
      <div className="h-full w-full overflow-y-auto px-2 pb-2" {...TOUR_LOCK_SCROLL_PROPS}>
        <ErrorSummaryBanner />
        <AnimatePresence initial mode="sync">
          {playground.messages.map((message: IMessageStore, index: number) => {
            const tourId = tourIdForMessageIndex(index);
            const messageContent =
              message.role === "placeholder" ? (
                <PlaceholderContainer
                  message={message}
                  index={index}
                  readonly={playground.readonly}
                />
              ) : (
                <Message
                  message={message}
                  index={index}
                  isCompletion={!playground.isChat}
                  isFirst={index === 0}
                  readonly={playground.readonly}
                />
              );

            return (
              <AnimatedElement key={`${message.id}-${message.role}`} className="h-full">
                <div className="h-full w-full">
                  {tourId ? (
                    <TourTargetContainer id={tourId} className="block h-full w-full">
                      {messageContent}
                    </TourTargetContainer>
                  ) : (
                    messageContent
                  )}
                </div>
              </AnimatedElement>
            );
          })}
          {shouldShowPlaceholder && (
            <AnimatedElement key="placeholder" className="h-full">
              <div className="h-full w-full">
                <AddNewMessageBlock />
              </div>
            </AnimatedElement>
          )}
        </AnimatePresence>
        <div ref={messagesEndRef} />
      </div>
    );
  }

  // ── Chat mode with drag and drop ────────────────────────────────────
  //
  // Key design decisions (Notion-inspired):
  //
  // 1. **No AnimatePresence / AnimatedElement inside Draggable.**
  //    framer-motion layout animations fight with @hello-pangea/dnd's
  //    transform-based positioning and cause jitter / incorrect offsets.
  //
  // 2. **No position:fixed override.**
  //    The library already handles fixed-positioning of the drag clone.
  //    Overriding it causes double-offset bugs.
  //
  // 3. **No scale / rotate on the drag clone.**
  //    Transforms change the bounding box, which confuses drop-zone hit
  //    testing. Instead we use a subtle shadow + slight opacity change,
  //    exactly like Notion's block drag.
  //
  // 4. **Instant drop animation.**
  //    The library's default ~200 ms drop ease-out is overridden with
  //    `transitionDuration: 0.001s` so the reorder feels optimistic.
  //
  return (
    <DragDropContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragUpdate={handleDragUpdate}
    >
      <Droppable droppableId="messages">
        {provided => (
          <div
            ref={el => {
              provided.innerRef(el);
              droppableRef.current = el;
            }}
            {...provided.droppableProps}
            {...TOUR_LOCK_SCROLL_PROPS}
            className="h-full w-full overflow-y-auto pb-2"
            style={{ minHeight: "100%" }}
            role="list"
            aria-label="Chat messages - drag to reorder"
          >
            <ErrorSummaryBanner />

            <Observer>
              {() => (
                <>
                  {playground.messages.map((message: IMessageStore, index: number) => (
                    <Draggable
                      key={`${message.id}-${message.role}`}
                      draggableId={message.id}
                      index={index}
                      isDragDisabled={dragDisabled}
                    >
                      {(draggableProvided, dragSnapshot) => {
                        // Notion-style: subtle shadow when dragging, nothing else.
                        let dragClassName = "";
                        if (dragSnapshot.isDragging) {
                          dragClassName = "z-50 rounded-lg bg-white shadow-lg ring-1 ring-black/5";
                        }

                        // Inlined instead of wrapping in <TourTargetContainer> — this div already
                        // owns the dnd ref/props and aria, which a wrapper would have to forward.
                        const tourId = tourIdForMessageIndex(index);

                        return (
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            className={dragClassName}
                            style={{
                              ...draggableProvided.draggableProps.style,
                              ...(dragSnapshot.isDragging && {
                                opacity: 0.95,
                              }),
                              // Skip the drop animation entirely so reorder
                              // feels instant / optimistic. Without this the
                              // library plays a ~200 ms ease-out before firing
                              // onDragEnd, which makes the UI feel sluggish.
                              ...(dragSnapshot.isDropAnimating && {
                                transitionDuration: "0.001s",
                              }),
                            }}
                            role="listitem"
                            aria-label={`Message ${index + 1}: ${message.role}${dragDisabled ? " (drag disabled)" : ""}`}
                            tabIndex={dragDisabled ? -1 : 0}
                            {...(tourId ? { id: tourId } : {})}
                          >
                            {ROLES.PLACEHOLDER === message.role ? (
                              <PlaceholderContainer
                                message={message}
                                index={index}
                                readonly={playground.readonly}
                                dragHandleProps={draggableProvided.dragHandleProps ?? undefined}
                              />
                            ) : (
                              <Message
                                message={message}
                                index={index}
                                isCompletion={false}
                                isFirst={index === 0}
                                readonly={playground.readonly}
                                dragHandleProps={draggableProvided.dragHandleProps ?? undefined}
                                isDragging={dragSnapshot.isDragging}
                              />
                            )}
                          </div>
                        );
                      }}
                    </Draggable>
                  ))}
                </>
              )}
            </Observer>

            {shouldShowPlaceholder && !draggingId && (
              <div className="w-full pb-1">
                <AddNewMessageBlock />
              </div>
            )}

            {/* Library-managed placeholder that maintains list height during drag */}
            {provided.placeholder}

            {/* Scroll anchor */}
            <div ref={messagesEndRef} className="h-1" />
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
});

const Messages = ({
  hideHeader = false,
  title,
  editorActions,
  footerLeftContent,
}: MessagesProps) => {
  const playground = usePlayground();

  return (
    <>
      <div className="flex h-full w-full flex-col">
        {!playground.readonly && !hideHeader && (
          <MessagesHeader title={title} editorActions={editorActions} />
        )}

        <GeminiPromptWarning />
        <div className="relative h-0 flex-1">
          <MessagesList />
        </div>
        {playground.readonly && <MessagesFooter leftContent={footerLeftContent} />}
      </div>
      <SnippetDialog />
    </>
  );
};

export default observer(withSnippetDialog(Messages));
